import { useState } from 'react';
import { Input } from 'antd';
import Icon from '@ant-design/icons';

import { useModal } from 'contexts/modalProvider.context';
import { sendSegment } from 'utils/segment';
import { shortenString } from 'utils/helpers';
import { LOCALE } from 'constants/intl-wrapper';
import { META_CATALOG_NAME } from 'constants/pixels';

import BRButton from 'components/BRButton/BRButton';
import CalendlyModal from 'components/Products/components/SllrStore/components/SllrSettings/components/SllrDomains/components/CalendlyModal';

import { ReactComponent as InputErrorIcon } from 'assets/bosta-icons/alert-circle-red.svg';
import { ReactComponent as CallIcon } from 'assets/bosta-icons/call.svg';
import { ReactComponent as CheckIcon } from 'assets/bosta-icons/Check.svg';
import { ReactComponent as Warning } from 'assets/bosta-icons/alert-circle.svg';

import './ConnectionSecondStep.less';

const CopyBtn = ({ name, intl, metaCatalogData, handleCopyCatalogUrl }) => {
  const { formatMessage } = intl;

  const [isCopied, setIsCopied] = useState(false);

  const handleOnClick = () => {
    setIsCopied(true);
    handleCopyCatalogUrl(metaCatalogData?.[name]);
    setTimeout(() => {
      setIsCopied(false);
    }, 3000);
  };

  return (
    <BRButton
      label={formatMessage({
        id: 'common.copy'
      })}
      type="link-color"
      prefixIcon={isCopied && <CheckIcon className="copy-check-icon" />}
      onClick={handleOnClick}
    />
  );
};

const ConnectionSecondStep = ({
  data: {
    pixelValue,
    setPixelValue,
    intl,
    apiKey,
    setErrors,
    errors,
    videoSrc,
    videoTimestamp,
    helpArticleLink,
    helpArticleLinkAr,
    name,
    icon,
    isLoading,
    metaCatalogData,
    iconClassName = '',
    ...rest
  }
}) => {
  const { formatMessage } = intl;
  const { openModal } = useModal();

  const isMetaCatalog = name === META_CATALOG_NAME;

  const handleCopyCatalogUrl = async (value) => {
    navigator.clipboard.writeText(value);
    await sendSegment('meta_catalogue_copy');
    // notify({
    //   msg: formatMessage({
    //     id: 'pixels.add_pixel_modal.meta_catalog_copied'
    //   }),
    //   type: 'success'
    // });
  };

  const handleChangePixelID = ({ target: { value } }) => {
    setPixelValue(value);
    setErrors({
      isVisible: false
    });
  };

  return (
    <div>
      <div className={`br-card__input-section ${name}`}>
        <div className="body-medium">
          {formatMessage({
            id: `pixels.pixel_input.${
              name === 'gtm'
                ? 'container_label'
                : isMetaCatalog
                ? 'meta_catalog_label'
                : 'label'
            }`
          })}
        </div>
        <Input
          name={apiKey}
          status={errors?.isVisible && 'error'}
          value={
            !isMetaCatalog
              ? pixelValue
              : shortenString(metaCatalogData?.url, 55) || ''
          }
          placeholder={formatMessage({
            id: 'pixels.errors.text_with_script'
          })}
          onChange={handleChangePixelID}
          suffix={
            errors?.isVisible ? (
              <InputErrorIcon />
            ) : isMetaCatalog ? (
              <CopyBtn
                intl={intl}
                name={'url'}
                metaCatalogData={metaCatalogData}
                handleCopyCatalogUrl={handleCopyCatalogUrl}
              />
            ) : undefined
          }
          disabled={isLoading || isMetaCatalog}
        />
        <div className="ant-form-item-explain-error">{errors?.text}</div>
        {isMetaCatalog && (
          <div className="meta-catalog-data">
            <Input
              name={apiKey}
              value={metaCatalogData?.username || ''}
              suffix={
                <CopyBtn
                  intl={intl}
                  name={'username'}
                  metaCatalogData={metaCatalogData}
                  handleCopyCatalogUrl={handleCopyCatalogUrl}
                />
              }
              disabled
            />
            <Input
              name={apiKey}
              value={metaCatalogData?.password || ''}
              suffix={
                <CopyBtn
                  intl={intl}
                  name={'password'}
                  metaCatalogData={metaCatalogData}
                  handleCopyCatalogUrl={handleCopyCatalogUrl}
                />
              }
              disabled
            />
          </div>
        )}
      </div>
      {!!videoSrc && (
        <div
          className={`br-card__article-and-video-section-container ${name}`}
          onClick={() => window.open(videoSrc, '_blank')}
        >
          <Icon
            component={icon}
            className={`br-card__article-and-video-section__background ${iconClassName}`}
          />
          <span className="br-card__article-and-video-section">
            <iframe
              width="156"
              height="124"
              src={videoSrc}
              title="YouTube video player"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            />
            <span>
              <div className="caption-lg text-gray">{videoTimestamp}</div>
              <div className="heading">
                {formatMessage(
                  { id: 'pixels.add_pixel_modal.how_to_install' },
                  { name: formatMessage({ id: `pixels.${[name]}.title` }) }
                )}
              </div>
            </span>
          </span>
          <Icon component={icon} className={iconClassName} />
        </div>
      )}
      <div className="br-card__support-section">
        <span>
          <span>
            <Warning />
          </span>
          <div className="body-medium">
            {formatMessage({ id: 'pixels.add_pixel_modal.facing_issues' })}
            <div className="br-card__support-section__description body">
              {!!helpArticleLink ? (
                <>
                  {formatMessage(
                    { id: 'pixels.add_pixel_modal.help_article_content' },
                    {
                      link: (
                        <a
                          className="br-card__support-section__help-article"
                          href={
                            intl.locale === LOCALE.EN
                              ? helpArticleLink
                              : helpArticleLinkAr
                          }
                        >
                          {formatMessage({
                            id: 'pixels.add_pixel_modal.help_article'
                          })}
                        </a>
                      )
                    }
                  )}
                  <div>
                    {formatMessage({
                      id: 'pixels.add_pixel_modal.still_have_problem'
                    })}{' '}
                    {formatMessage({
                      id: 'pixels.add_pixel_modal.schedule_a_call'
                    })}
                  </div>
                </>
              ) : (
                formatMessage({
                  id: 'pixels.add_pixel_modal.schedule_a_call'
                })
              )}
            </div>
          </div>
        </span>
        <BRButton
          label={formatMessage({
            id: 'products.sllr_store.setting_page.domains_tab.verify_modal.tip_action'
          })}
          className="button-lg"
          type="link-color"
          prefixIcon={<CallIcon />}
          onClick={() => {
            openModal(CalendlyModal);
          }}
        />
      </div>
    </div>
  );
};

export default ConnectionSecondStep;
